import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X, Mic, FileText, ListChecks, PenLine, Youtube, Film, MessageSquare, Library, LineChart, Image as ImageIcon, AudioLines, Gauge, Quote, Zap, ShieldCheck, RefreshCw, Layers, PhoneCall, MessageCircle, Infinity as InfinityIcon } from 'lucide-react';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';
import V2Hero from '../../components/v2/V2Hero';
import { HERO } from '../../data/heroImages';
import { PrimaryCTA, CTABlock } from '../../components/v2/CTAButton';

// -----------------------------------------------------------------------------
// HERO TESTIMONIAL — Juliette Karaman, VERBATIM extract of the two closing
// sentences from her review on /results (Results.js line 16). No editing.
// -----------------------------------------------------------------------------
const HERO_TESTIMONIAL = {
  quote: 'Less than 1 month from first speaking it was launched. Within 2 months we are ranking in the top 10% worldwide.',
  author: 'Juliette Karaman',
  role: 'Host, The Scrumptious Woman',
};

const ALL_DELIVERABLES = [
  { icon: Mic,           title: '1 podcast episode',          desc: 'Recorded by you, edited and published by us. Distributed to Spotify, Apple, Google.' },
  { icon: FileText,      title: '1 edited transcript',         desc: 'Cleaned for grammar and flow. Ready to use anywhere.' },
  { icon: ListChecks,    title: '1 tailored show notes page',  desc: 'Search-optimised, link-rich, on your site.' },
  { icon: PenLine,       title: '1 blog post',                 desc: 'A long-form piece anchored to your episode, on your domain, in your voice.' },
  { icon: Youtube,       title: '1 YouTube video + description', desc: 'Optimised title, description, tags, thumbnail.' },
  { icon: ImageIcon,     title: 'Eye-catching thumbnails',     desc: 'Custom podcast episode art + scroll-stopping YouTube thumbnails, designed weekly to match your brand.' },
  { icon: Film,          title: '3 Shorts per day (21/week)',  desc: 'Vertical, captioned, on-brand, scheduled across YouTube/Reels/TikTok.' },
  { icon: MessageSquare, title: '7 Community posts',           desc: 'One YouTube Community post per day, text, polls, image, to keep your channel warm.' },
  { icon: Library,       title: 'Business website updates',       desc: 'Your owned content library (Authority Hub), kept fresh and interlinked every week.' },
  { icon: LineChart,     title: 'Google Search Console monitoring', desc: 'Weekly GSC + Bing Webmaster review, target-keyword rankings, indexing/coverage, click-through rate, backlinks, crawl errors. Reported at your weekly check-in.' },
  { icon: AudioLines,    title: 'Voice & AI search visibility',      desc: 'Optimised to be cited by Siri, Hey Google, Alexa, ChatGPT, Perplexity, Claude and Gemini, not just classic Google. Structured data + schema + transcript optimisation, done for you.' },
  { icon: Gauge,         title: 'Site performance monitoring',       desc: 'Core Web Vitals, page speed, mobile experience and uptime watched weekly. Alerts + quick-fix plan if anything slips.' },
];

const NOT_FIT = [
  'You want one-off editing only.',
  'You’re not ready to record weekly.',
  'You want viral hacks instead of consistent publishing.',
];

export default function ProPlan() {
  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="The Pro Plan: Done-for-You Podcast & Content | Audio & Co."
        description="Record once a week and we ship your podcast, YouTube, Shorts, articles and website updates. £280 a week, 4-week minimum, cancel any time."
        keywords={['done for you podcast production uk', 'podcast visibility package', 'content repurposing service uk', 'weekly content service', 'podcast production agency uk', 'youtube management for small business', 'content marketing agency for coaches']}
        url="https://audioandco.com/podcast-visibility-package"
      />

      {/* HERO — rebuilt Sep 2026 per rebuild spec:
          - Public name is now "Podcast Visibility Package" (Pro Plan
            appears once, small, as a label).
          - Weekly £280 kept in the SECOND-line pricing detail; monthly
            headline is what shows first below.
          - Trust strip + Juliette testimonial kept from prior work. */}
      <V2Hero image={HERO.homePodcastDesk} testId="proplan-hero">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">The Podcast Visibility Package <span className="text-[#94A3B8] font-normal normal-case tracking-normal ml-1">(previously the Pro Plan)</span></p>
          <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3">
            Record once a week.
            <span className="block text-[#BC516F] mt-2">We ship the rest.</span>
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">
            Every week we turn your one 30-minute recording into <b>1 podcast episode, 1 YouTube video, 21 Shorts, 7 Community posts, 1 blog and 1 email</b>. Fully done for you. Fully consistent.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <CTABlock size="lg" testIdPrefix="proplan-hero-cta" secondaryHref="#whats-included" />
            <div className="text-sm text-[#475569]">
              <b className="text-[#1E2A44]">From &pound;1,213/month</b> &nbsp;(billed monthly)<br />
              <span className="text-[#475569]/80 text-xs">Equivalent to &pound;280/week. 4-week minimum, cancel any time.</span>
            </div>
          </div>
          {/* Trust strip — swap for a real client quote when we have permission.
              Deliberately concrete + honest: no invented ratings, no fake logos. */}
          <div className="mt-8 border-l-4 border-[#BC516F] bg-white/70 backdrop-blur-sm rounded-r-[10px] px-5 py-4 max-w-2xl" data-testid="proplan-trust-strip">
            <div className="flex items-center gap-3 text-sm text-[#475569]">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span className="inline-flex items-center gap-1.5 text-[#1E2A44] font-semibold"><Zap className="w-4 h-4 text-[#BC516F]" fill="currentColor" />Weekly turnaround, guaranteed</span>
                <span className="inline-flex items-center gap-1.5 text-[#1E2A44] font-semibold"><ShieldCheck className="w-4 h-4 text-[#A7468A]" />Zero editing, zero tech</span>
                <span className="inline-flex items-center gap-1.5 text-[#1E2A44] font-semibold"><RefreshCw className="w-4 h-4 text-[#81162A]" />Cancel anytime after week 4</span>
              </div>
            </div>
          </div>
          {/* Optional client testimonial. Renders only when HERO_TESTIMONIAL.quote is set. */}
          {HERO_TESTIMONIAL.quote && (
            <figure className="mt-6 bg-white/90 backdrop-blur-sm border border-[#E2E8F0] rounded-[14px] p-5 max-w-2xl shadow-[0_10px_30px_rgba(30,42,68,0.06)]" data-testid="proplan-hero-testimonial">
              <Quote className="w-6 h-6 text-[#BC516F] mb-2" />
              <blockquote className="text-[#1E2A44] font-display font-semibold text-lg leading-snug">
                &ldquo;{HERO_TESTIMONIAL.quote}&rdquo;
              </blockquote>
              {(HERO_TESTIMONIAL.author || HERO_TESTIMONIAL.role) && (
                <figcaption className="mt-3 text-sm text-[#475569]">
                  {HERO_TESTIMONIAL.author && <span className="font-semibold text-[#1E2A44]">{HERO_TESTIMONIAL.author}</span>}
                  {HERO_TESTIMONIAL.author && HERO_TESTIMONIAL.role && <span> · </span>}
                  {HERO_TESTIMONIAL.role && <span>{HERO_TESTIMONIAL.role}</span>}
                </figcaption>
              )}
            </figure>
          )}
        </div>
      </V2Hero>

      {/* PRO PLAN PROMO VIDEO */}
      <Section bg="white" testId="proplan-video">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest text-center">Watch: 90 seconds</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-3 text-center">
            The Pro Plan in your own time.
          </h2>
          <p className="text-[#64748B] mt-3 max-w-2xl mx-auto text-center">
            A quick tour of what happens every week when you plug in to the Pro Plan.
          </p>
          <div className="mt-8 rounded-[14px] overflow-hidden border border-[#E2E8F0] shadow-[0_18px_44px_rgba(30,42,68,0.12)] bg-black">
            <video
              controls
              playsInline
              preload="metadata"
              poster={HERO.homePodcastDesk.src}
              className="w-full aspect-video block"
              data-testid="proplan-promo-video"
            >
              <source src="/videos/audioandco-pro-plan.mp4" type="video/mp4" />
              Your browser does not support HTML5 video.
            </video>
          </div>
        </div>
      </Section>

      {/* FULL DELIVERABLES LIST — premium dark section with cascading spotlight */}
      <Section bg="dark" testId="proplan-deliverables" innerClassName="!py-20 sm:!py-24 relative">
        <span id="whats-included" className="block -mt-24 pt-24" aria-hidden="true" />

        {/* Decorative plum wash behind heading */}
        <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-[#A7468A]/20 blur-3xl pointer-events-none v2-blob" aria-hidden="true" />
        <div className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full bg-[#BC516F]/15 blur-3xl pointer-events-none v2-blob" style={{ animationDelay: '4s' }} aria-hidden="true" />

        <div className="relative max-w-3xl">
          <p className="text-[#E0A8B8] font-semibold text-sm uppercase tracking-[0.2em] flex items-center gap-3">
            <span className="w-8 h-px bg-[#E0A8B8]/60" />
            The full weekly engine
          </p>
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.05]">
            Every week, we ship <span className="v2-shimmer">thirty-one outputs</span> in your voice.
          </h2>
          <p className="text-white/70 mt-5 leading-relaxed text-lg max-w-2xl">
            One recording. A full week of visibility. Watch the highlight sweep the deck &mdash; every card is something you no longer have to build yourself.
          </p>
        </div>

        <div className="relative mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {ALL_DELIVERABLES.map((d, i) => {
            const Icon = d.icon;
            const num = String(i + 1).padStart(2, '0');
            return (
              <div
                key={i}
                className="v2-cascade group relative bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-[16px] p-6 sm:p-7 flex flex-col overflow-hidden"
                style={{ '--card-index': i }}
                data-testid={`deliverable-${i}`}
              >
                {/* Editorial serif index number */}
                <div className="absolute top-4 right-5 v2-serif-accent text-white/25 text-3xl select-none pointer-events-none">
                  {num}
                </div>

                {/* Rose→plum gradient icon orb */}
                <div
                  className="v2-cascade-orb w-12 h-12 rounded-[12px] flex items-center justify-center shadow-[0_10px_24px_-6px_rgba(188,81,111,0.5)]"
                  style={{ background: 'linear-gradient(135deg, #BC516F 0%, #A7468A 55%, #81162A 100%)' }}
                >
                  <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                </div>

                <h3 className="font-display font-semibold text-white text-lg leading-tight mt-5">{d.title}</h3>
                <p className="text-white/65 mt-2 leading-relaxed text-[15px] flex-1">{d.desc}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* HOW IT WORKS, DETAILED */}
      <Section bg="bg" testId="proplan-how">
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight max-w-2xl">How a Pro Plan week actually feels.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { n: 1, t: 'Monday, record', d: 'You join a 30-60 minute recording session. We give you the topic structure and questions. You bring the voice.' },
            { n: 2, t: 'Tuesday–Thursday, we produce', d: 'Edit, transcribe, show notes, blog, thumbnail, Shorts, Community posts, Hub update, SEO check. All done in the background.' },
            { n: 3, t: 'Friday, publish + check-in', d: 'Everything goes live across your channels. We send you a 1-page summary + a 15-min check-in.' },
          ].map(s => (
            <div key={s.n} className="bg-white rounded-[14px] p-6 sm:p-7 border border-[#E2E8F0] shadow-[0_10px_30px_rgba(30,42,68,0.04)]">
              <div className="font-display text-4xl font-bold text-[#BC516F] leading-none">0{s.n}.</div>
              <h3 className="font-display font-semibold text-[#1E2A44] text-xl mt-3">{s.t}</h3>
              <p className="text-[#475569] mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* NOT A FIT */}
      <Section bg="white" testId="proplan-not-fit">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Honest qualifier</p>
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">Not a fit if…</h2>
            <p className="text-[#64748B] mt-4 leading-relaxed">
              We’d rather tell you now than three weeks in. The Pro Plan is built for one specific
              type of business owner. If any of these are you, the call won’t go anywhere good.
            </p>
          </div>
          <ul className="space-y-3">
            {NOT_FIT.map((p, i) => (
              <li key={i} className="flex items-start gap-3 bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-5">
                <X className="w-5 h-5 text-[#BC516F] shrink-0 mt-0.5" />
                <span className="text-[#475569]">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* PRICING REPEAT */}
      <Section bg="bg" testId="proplan-pricing">
        <div className="max-w-2xl mx-auto bg-white border border-[#E2E8F0] rounded-[14px] p-8 sm:p-10 text-center shadow-[0_10px_30px_rgba(30,42,68,0.06)]">
          <div className="font-display text-5xl sm:text-6xl font-bold text-[#1E2A44]">
            £280<span className="text-2xl text-[#64748B] font-medium">/week</span>
          </div>
          <p className="text-[#64748B] mt-2">4-week minimum &nbsp;•&nbsp; cancel any time &nbsp;•&nbsp; billed monthly</p>
          <p className="text-sm text-[#64748B] mt-1">Approx. £1,213/month &nbsp;•&nbsp; 4-week minimum &nbsp;•&nbsp; cancel any time</p>
          <ul className="mt-6 space-y-2.5 text-left max-w-md mx-auto">
            {[
              { icon: Layers,       text: 'Everything in the weekly deliverables list' },
              { icon: PhoneCall,    text: 'Weekly 15-min check-in call' },
              { icon: MessageCircle,text: 'Slack/email access for in-between questions' },
              { icon: InfinityIcon, text: 'Cancel any time after week 4 — 6 months recommended for momentum' },
            ].map((b, i) => {
              const Ico = b.icon;
              return (
                <li key={i} className="flex items-start gap-2.5 text-[#475569]">
                  <span className="w-6 h-6 shrink-0 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #BC516F 0%, #A7468A 100%)' }}>
                    <Ico className="w-3.5 h-3.5 text-white" />
                  </span>
                  <span className="mt-0.5">{b.text}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-8">
            <CTABlock size="lg" align="center" testIdPrefix="proplan-pricing-cta" secondaryHref="#whats-included" />
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">Six months from now you could be everywhere.</h2>
          <p className="text-white/75 mt-4 text-lg">Or you could not. Either way, the call is free.</p>
          <div className="mt-7 flex justify-center">
            <CTABlock size="lg" align="center" invert testIdPrefix="proplan-final-cta" secondaryHref="#whats-included" />
          </div>
        </div>
      </Section>
    </main>
  );
}
